
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

/**
 * Custom hook to handle form deletion for PEO providers
 */
export const useProviderFormDelete = (
  providerId?: string,
  providerName?: string,
  onDeleteProvider?: (providerId: string) => Promise<boolean>,
  onSuccess?: () => void
) => {
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const { toast } = useToast();

  const handleProviderDelete = async () => {
    if (!providerId) {
      console.error('No provider ID provided for deletion');
      toast({
        title: 'Error',
        description: 'Cannot delete provider: No provider ID found',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsDeleteInProgress(true);
      console.log(`Deleting PEO provider with ID: ${providerId}`);

      if (onDeleteProvider) {
        console.log('Using provided onDeleteProvider function');
        const success = await onDeleteProvider(providerId);
        
        if (success) {
          console.log('Delete successful via onDeleteProvider');
          if (onSuccess) {
            onSuccess();
          }
        } else {
          console.log('Delete unsuccessful via onDeleteProvider');
          toast({
            title: 'Error',
            description: 'Failed to delete PEO provider. Please try again.',
            variant: 'destructive',
          });
        }
        return;
      }

      // Fallback to direct deletion if no onDeleteProvider function is provided
      console.log('No onDeleteProvider function provided, using direct deletion');
      const { error } = await supabase
        .rpc('delete_peo_provider', { provider_id: providerId });

      if (error) {
        console.error('Error deleting provider:', error);
        toast({
          title: 'Error',
          description: `Failed to delete provider: ${error.message}`,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Success',
        description: `Provider "${providerName}" deleted successfully`,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error during provider deletion:', error);
      toast({
        title: 'Error',
        description: `Failed to delete provider: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setIsDeleteInProgress(false);
    }
  };

  return {
    handleProviderDelete,
    isDeleteInProgress,
  };
};
